/**
 * Swan Branding Kit — palette engine.
 *
 * Pure functions. Given a school's chosen brand colour(s), derive a COMPLETE
 * daisyUI theme token set for BOTH light and dark mode — legible for any input
 * colour, because everything is computed in OKLCH (perceptual lightness/chroma/hue)
 * rather than raw sRGB.
 *
 * daisyUI 5 themes are just CSS custom properties (--color-primary, --color-base-100,
 * --color-base-content, …). We output those as a plain map; the runtime layer injects
 * them under a `[data-theme="school-*"]` rule and the whole app recolours with no
 * component changes.
 *
 * Design rules (see project_branding_kit_whitelabel memory):
 *  - Brand tokens (primary/secondary/accent) come from the school's colours.
 *  - Content (text-on-colour) is auto-picked for contrast — never illegible.
 *  - Base surfaces are near-white (light) / near-black (dark), tinted subtly with the
 *    brand hue — the same trick Swan uses on its own teal-tinted greys. Not flooded.
 *  - Functional colours (info/success/warning/error) stay recognisable and are NOT
 *    recoloured to the brand — an error must still read as an error.
 *  - Shape tokens (radius/size/border) mirror Swan's so branded themes feel native.
 */

/* ─────────────────────────── colour maths ─────────────────────────── */
// All conversions per Björn Ottosson's OKLab (https://bottosson.github.io/posts/oklab/).

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const clamp = (x, lo, hi) => (x < lo ? lo : x > hi ? hi : x);

/** "#RGB" | "#RRGGBB" | "rgb(...)" → {r,g,b} in 0..1. Returns null if unparseable. */
export function parseColor(input) {
  if (typeof input !== 'string') return null;
  let s = input.trim();
  const hexMatch = s.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
  }
  const rgb = s.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (rgb) return { r: +rgb[1] / 255, g: +rgb[2] / 255, b: +rgb[3] / 255 };
  return null;
}

const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const linearToSrgb = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);

function rgbToOklab({ r, g, b }) {
  const lr = srgbToLinear(r), lg = srgbToLinear(g), lb = srgbToLinear(b);
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;
  const l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

function oklabToRgb({ L, a, b }) {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3, m = m_ ** 3, s = s_ ** 3;
  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return {
    r: clamp01(linearToSrgb(lr)),
    g: clamp01(linearToSrgb(lg)),
    b: clamp01(linearToSrgb(lb)),
  };
}

/** {r,g,b} 0..1 → {L (0..1), C, H (deg)} */
export function rgbToOklch(rgb) {
  const { L, a, b } = rgbToOklab(rgb);
  const C = Math.sqrt(a * a + b * b);
  let H = (Math.atan2(b, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { L, C, H };
}

export function hexToOklch(hex) {
  const rgb = parseColor(hex);
  return rgb ? rgbToOklch(rgb) : null;
}

const oklchToRgb = ({ L, C, H }) => {
  const h = (H * Math.PI) / 180;
  return oklabToRgb({ L, a: C * Math.cos(h), b: C * Math.sin(h) });
};

/** {L,C,H} → "oklch(L% C H)" string daisyUI understands. */
export function oklchCss({ L, C, H }) {
  const l = +(clamp01(L) * 100).toFixed(2);
  const c = +Math.max(0, C).toFixed(4);
  const h = +(((H % 360) + 360) % 360).toFixed(2);
  return `oklch(${l}% ${c} ${h})`;
}

/** WCAG relative luminance from {r,g,b} 0..1. */
function luminance({ r, g, b }) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/** Contrast ratio between two {r,g,b}. */
export function contrastRatio(a, b) {
  const la = luminance(a), lb = luminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Pick a legible foreground for a given background colour (as {L,C,H}).
 * Prefers a near-white or near-ink tinted faintly toward the background hue
 * (so it never looks like pure #fff / #000). If the mid-lightness of a vivid
 * background defeats both, it drives the winning direction darker/lighter until
 * it clears the AA threshold (4.5) — guaranteeing legible text on any colour.
 */
export function pickContent(bgOklch, target = 4.5) {
  const bg = oklchToRgb(bgOklch);
  const at = (L, C) => contrastRatio(bg, oklchToRgb({ L, C, H: bgOklch.H }));

  const white = { L: 0.98, C: 0.01, H: bgOklch.H };
  const ink = { L: 0.22, C: 0.03, H: bgOklch.H };
  const cWhite = at(white.L, white.C);
  const cInk = at(ink.L, ink.C);

  if (Math.max(cWhite, cInk) >= target) return cWhite >= cInk ? white : ink;

  // Neither fixed candidate clears the bar — push the better direction to an
  // extreme (pure white ↑ / pure black ↓), dropping the hue tint so it can go
  // as far as needed.
  return cInk >= cWhite
    ? { L: 0.15, C: 0.0, H: bgOklch.H }
    : { L: 1.0, C: 0.0, H: bgOklch.H };
}

/* ───────────────────────── palette derivation ───────────────────────── */

// Functional colours are shared across all schools — an error is always red, etc.
// Copied from Swan's own theme so branded themes match the app's status language.
const FUNCTIONAL = {
  '--color-info': 'oklch(74% 0.16 232.661)',
  '--color-info-content': 'oklch(29% 0.066 243.157)',
  '--color-success': 'oklch(76% 0.177 163.223)',
  '--color-success-content': 'oklch(37% 0.077 168.94)',
  '--color-warning': 'oklch(82% 0.189 84.429)',
  '--color-warning-content': 'oklch(41% 0.112 45.904)',
  '--color-error': 'oklch(65% 0.23 25)',
  '--color-error-content': '#ffffff',
};

const SHAPE = {
  '--radius-selector': '0.5rem',
  '--radius-field': '0.75rem',
  '--radius-box': '1rem',
  '--size-selector': '0.25rem',
  '--size-field': '0.28125rem',
  '--border': '0.5px',
  '--noise': '0',
};

/** Lightness-shift an OKLCH colour, keeping its hue and chroma. */
function shadeL(oklch, dL) {
  return { L: clamp01(oklch.L + dL), C: oklch.C, H: oklch.H };
}

/**
 * Force a colour into a genuinely dark band for use as a dark-mode page. Keeps
 * the hue, trims chroma a touch so it reads as ink not neon. A medium colour1
 * (e.g. a mid maroon) still yields a properly dark page.
 */
function clampDark(oklch) {
  return { L: clamp(oklch.L, 0.14, 0.24), C: Math.min(oklch.C, 0.05), H: oklch.H };
}

/** A near-white page carrying only a whisper of the given hue — never flooded. */
function nearWhite(oklch) {
  return { L: 0.98, C: Math.min(oklch.C, 0.008), H: oklch.H };
}

/**
 * The accent (colour2) is the outline colour in BOTH modes, so a school's
 * hairlines always carry its accent — coherent with the primary buttons. On the
 * dark page the accent can itself be dark (a deep maroon/navy) and vanish against
 * the clamped-dark surface, so lift its lightness to a legible floor. Naturally
 * light accents (gold, teal…) pass through untouched.
 */
function outlineForDark(oklch) {
  return { L: Math.max(oklch.L, 0.5), C: oklch.C, H: oklch.H };
}

/** {color, color-content} token pair for a filled element (button/badge). */
function filled(prefix, colorOklch) {
  return {
    [`--color-${prefix}`]: oklchCss(colorOklch),
    [`--color-${prefix}-content`]: oklchCss(pickContent(colorOklch)),
  };
}

/**
 * Build one mode from the three brand colours.
 *   colour1 = dark · colour2 = accent · colour3 = light
 *
 * Light mode: page = near-white (whispered toward colour3), primary button =
 *             colour1, secondary/accent + outlines = colour2.
 * Dark mode:  page = colour1 clamped into a genuinely dark band, primary button
 *             = colour2, secondary/accent = colour2, outlines = colour2 too
 *             (lightness-lifted so a dark accent stays visible on the dark page).
 *
 * The page stays neutral in both modes; the brand shows on buttons, badges and
 * outlines. Text is NOT one of the brand colours — it's auto-derived for
 * legibility against the background. White or black are valid picks.
 */
function buildMode(mode, { dark, accent, light }) {
  const isDark = mode === 'dark';
  const primary = isDark ? accent : dark;   // hero button swaps by mode
  const outline = isDark ? outlineForDark(accent) : accent;  // colour2 in both modes

  // Page surface: a near-white page in light mode (whispered toward the school's
  // light hue, never flooded) and colour1 forced into a genuinely dark band in
  // dark mode. The brand shows on buttons, badges and outlines — not the whole page.
  const surface = isDark ? clampDark(dark) : nearWhite(light);

  const base100 = surface;
  const base200 = shadeL(surface, isDark ? 0.04 : -0.03);
  // base-300 is daisyUI's border token — every hairline/outline in the app reads
  // it. Per spec, outlines ARE a brand colour: colour2 (the accent) in BOTH modes,
  // lightness-lifted on the dark page. So base-300 is the real outline colour, not
  // a tint of the page.
  const base300 = outline;
  const neutral = isDark ? shadeL(surface, 0.14) : dark;

  return {
    'color-scheme': mode,
    '--color-base-100': oklchCss(base100),
    '--color-base-200': oklchCss(base200),
    '--color-base-300': oklchCss(base300),
    '--color-base-content': oklchCss(pickContent(base100)),
    '--color-neutral': oklchCss(neutral),
    '--color-neutral-content': oklchCss(pickContent(neutral)),
    ...filled('primary', primary),
    ...filled('secondary', accent),
    ...filled('accent', accent),
    ...FUNCTIONAL,
    ...SHAPE,
    '--depth': isDark ? '0' : '1',
  };
}

/**
 * deriveSchoolTheme — the public entry point.
 *
 * @param {{dark?:string, accent?:string, light?:string}} colours  hex/rgb strings
 *   dark   = colour 1 — page in dark mode; primary button + text in light mode
 *   accent = colour 2 — the accent: secondary/accent everywhere, primary in dark
 *   light  = colour 3 — page in light mode; text in dark mode
 * @returns {{light:Object, dark:Object}|null}  token maps, or null if the
 *   structural dark/light pair is invalid.
 */
export function deriveSchoolTheme(colours = {}) {
  const dark = hexToOklch(colours.dark);
  const light = hexToOklch(colours.light);
  if (!dark || !light) return null;

  // Accent is the one vivid colour; if absent, spin one off the dark hue.
  const accent =
    hexToOklch(colours.accent) ||
    { L: 0.6, C: Math.max(dark.C, 0.14), H: (dark.H + 180) % 360 };

  const seeds = { dark, accent, light };
  return {
    light: buildMode('light', seeds),
    dark: buildMode('dark', seeds),
  };
}

/** Serialize one mode's token map into a CSS rule body (for a `<style>` injection). */
export function tokensToCss(tokens) {
  return Object.entries(tokens)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
}

/** daisyUI theme names for a school's runtime light/dark themes. */
export const SCHOOL_THEME = { light: 'swan-school-light', dark: 'swan-school-dark' };

/**
 * Build the full `<style>` text that registers a school's derived theme as two
 * daisyUI themes (`swan-school-light` / `swan-school-dark`). Injecting this and
 * setting `data-theme` to one of the names recolours the entire app.
 */
export function buildSchoolThemeCss(derived) {
  if (!derived) return '';
  // base-300 is the brand OUTLINE colour (colour2 / gold). daisyUI reuses base-300
  // as BOTH the border token (`border-base-300` — every card hairline, must stay
  // gold) AND a surface FILL token (`bg-base-300`, `.skeleton`, search/filter
  // wells). On a branded school the fill side floods those surfaces with gold.
  //
  // Fix it once, at the theme level: inside the branded themes only, repoint every
  // base-300 FILL to the neutral base-200 step. Borders are untouched (they read
  // the CSS variable, not these utility classes), so hairlines keep colour2 while
  // panels, wells and skeletons go grey. Non-branded schools never load this CSS,
  // so their default base-300 contrast is preserved.
  const L = `[data-theme="${SCHOOL_THEME.light}"]`;
  const D = `[data-theme="${SCHOOL_THEME.dark}"]`;
  const neutral = 'var(--color-base-200)';
  // color-mix so opacity variants (bg-base-300/60 etc.) fade the NEUTRAL step, not gold.
  const mix = (pct) => `color-mix(in oklch, var(--color-base-200) ${pct}%, transparent)`;
  const opacities = [30, 40, 50, 60, 80];
  const rules = [];
  // .skeleton fills via the base-300 variable directly (not a utility class).
  rules.push(`${L} .skeleton,\n${D} .skeleton { background-color: ${neutral}; }`);
  // Solid fill utility: `bg-base-300`.
  rules.push(`${L} [class~="bg-base-300"],\n${D} [class~="bg-base-300"] { background-color: ${neutral}; }`);
  // Hover fill utility: `hover:bg-base-300`.
  rules.push(`${L} [class~="hover:bg-base-300"]:hover,\n${D} [class~="hover:bg-base-300"]:hover { background-color: ${neutral}; }`);
  // Opacity fill variants: `bg-base-300/NN` and `hover:bg-base-300/NN`.
  for (const p of opacities) {
    rules.push(`${L} [class~="bg-base-300/${p}"],\n${D} [class~="bg-base-300/${p}"] { background-color: ${mix(p)}; }`);
    rules.push(`${L} [class~="hover:bg-base-300/${p}"]:hover,\n${D} [class~="hover:bg-base-300/${p}"]:hover { background-color: ${mix(p)}; }`);
  }
  const fillFix = rules.join('\n') + '\n';
  return (
    `${L} {\n${tokensToCss(derived.light)}\n}\n` +
    `${D} {\n${tokensToCss(derived.dark)}\n}\n` +
    fillFix
  );
}
